import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getAllModules, moduleSupportsDetailPages, getModuleServiceType, getModuleIdField, parseDetailUrl, ModuleItem } from '../../utils/memoryPalaceUtils';
import { peopleService, Person } from '../../services/peopleService';
import { NovelService, Novel } from '../../services/novelService';
import { NotesService, Note } from '../../services/notesService';
import { KnowledgeCategoryService, KnowledgeCategory } from '../../services/knowledgeCategoryService';
import { getWhiteboards, Whiteboard } from '../../services/whiteboardService';
import { bookService } from '../../services/bookService';
import { Book } from '../../types/Book';
import './MemoryPalace.css';

export interface PlaceObject {
  id: string;
  type: string;
  name: string;
  description?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  connectedPlaceId?: number;
  connectionDirection?: 'outgoing' | 'incoming'; // 'outgoing' = to, 'incoming' = from
  linkedModulePath?: string; // e.g., "/whiteboard"
  linkedItemId?: string; // e.g., "123" (the whiteboard ID or other item ID)
  image_url?: string;
  image_prompt?: string;
}

const PREDEFINED_OBJECT_TYPES = [
  'chair',
  'table',
  'whiteboard',
  'picture frame',
  'bookshelf',
  'book',
  'statue',
  'door',
  'window',
  'lamp',
  'other'
];

interface ObjectFormProps {
  object?: PlaceObject | null;
  onSave: (object: PlaceObject) => void;
  onCancel: () => void;
  initialPosition?: { x: number; y: number };
}

const ObjectForm: React.FC<ObjectFormProps> = ({ object, onSave, onCancel, initialPosition }) => {
  const { user } = useAuth();
  const [type, setType] = useState('chair');
  const [customType, setCustomType] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [width, setWidth] = useState(100);
  const [height, setHeight] = useState(60);
  const [error, setError] = useState<string | null>(null);
  
  // Module linking state
  const [linkedModuleType, setLinkedModuleType] = useState<'none' | 'internal'>('none');
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [modules] = useState<ModuleItem[]>(getAllModules());
  
  // Item selection state for modules with detail pages
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [itemSearchResults, setItemSearchResults] = useState<(Person | Novel | Note | KnowledgeCategory | Whiteboard | Book)[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [manualItemId, setManualItemId] = useState('');
  const [searchingItems, setSearchingItems] = useState(false);
  const [showItemSearch, setShowItemSearch] = useState(false);

  // Search for items when module changes or search term changes
  useEffect(() => {
    if (!selectedModule || !moduleSupportsDetailPages(selectedModule) || !user) {
      setShowItemSearch(false);
      setItemSearchResults([]);
      setItemSearchTerm('');
      return;
    }

    setShowItemSearch(true);
    
    const searchItems = async () => {
      if (!itemSearchTerm.trim()) {
        setItemSearchResults([]);
        return;
      }

      setSearchingItems(true);
      try {
        const serviceType = getModuleServiceType(selectedModule);
        if (serviceType === 'people') {
          const response = await peopleService.getPeopleByUserId(user.id, itemSearchTerm);
          if (response.success) {
            setItemSearchResults(response.people);
          }
        } else if (serviceType === 'novel') {
          const novels = await NovelService.getUserNovels(user.id, { limit: 50, sort_order: 'DESC' });
          const filtered = novels.filter(novel => 
            novel.title.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
            novel.genre?.toLowerCase().includes(itemSearchTerm.toLowerCase())
          );
          setItemSearchResults(filtered);
        } else if (serviceType === 'notes') {
          const response = await NotesService.getUserNotes(user.id, { limit: 50, sort_order: 'DESC' });
          const filtered = response.data.filter(note => 
            note.title.toLowerCase().includes(itemSearchTerm.toLowerCase())
          );
          setItemSearchResults(filtered);
        } else if (serviceType === 'knowledge') {
          const response = await KnowledgeCategoryService.getCategories();
          const filtered = response.categories.filter(category => 
            category.category_name.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
            category.description?.toLowerCase().includes(itemSearchTerm.toLowerCase())
          );
          setItemSearchResults(filtered);
        } else if (serviceType === 'whiteboard') {
          const whiteboards = await getWhiteboards();
          const filtered = whiteboards.filter(wb => 
            wb.name.toLowerCase().includes(itemSearchTerm.toLowerCase())
          );
          setItemSearchResults(filtered);
        } else if (serviceType === 'book') {
          const books = await bookService.searchBooks(itemSearchTerm);
          setItemSearchResults(books);
        }
      } catch (error) {
        console.error('Error searching items:', error);
        setItemSearchResults([]);
      } finally {
        setSearchingItems(false);
      }
    };

    const debounceTimer = setTimeout(searchItems, 300);
    return () => clearTimeout(debounceTimer);
  }, [selectedModule, itemSearchTerm, user]);

  useEffect(() => {
    if (object) {
      setName(object.name);
      setDescription(object.description || '');
      setWidth(object.width);
      setHeight(object.height);
      
      // Check if type is in predefined list
      if (PREDEFINED_OBJECT_TYPES.includes(object.type)) {
        setType(object.type);
        setCustomType('');
      } else {
        setType('other');
        setCustomType(object.type);
      }
      
      // Handle linked module
      if (object.linkedModulePath && object.linkedItemId) {
        setLinkedModuleType('internal');
        setSelectedModule(object.linkedModulePath);
        setSelectedItemId(object.linkedItemId);
        setManualItemId(object.linkedItemId);
      } else {
        setLinkedModuleType('none');
        setSelectedModule('');
        setSelectedItemId('');
        setManualItemId('');
      }
    } else {
      // Default values for new object
      setName('');
      setDescription('');
      setType('chair');
      setCustomType('');
      setWidth(100);
      setHeight(60);
      setLinkedModuleType('none');
      setSelectedModule('');
      setSelectedItemId('');
      setManualItemId('');
    }
  }, [object]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (type === 'other' && !customType.trim()) {
      setError('Please enter a custom type');
      return;
    }

    // Validate width and height
    if (!width || width < 20 || width > 500) {
      setError('Width must be between 20 and 500 pixels');
      return;
    }

    if (!height || height < 20 || height > 500) {
      setError('Height must be between 20 and 500 pixels');
      return;
    }

    const finalType = type === 'other' ? customType.trim() : type;

    const objectData: PlaceObject = {
      id: object?.id || `obj-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: finalType,
      name: name.trim() || '',
      description: description.trim() || undefined,
      x: object?.x ?? initialPosition?.x ?? 50,
      y: object?.y ?? initialPosition?.y ?? 50,
      width: width,
      height: height,
      connectedPlaceId: object?.connectedPlaceId,
      connectionDirection: object?.connectionDirection,
      linkedModulePath: object?.linkedModulePath,
      linkedItemId: object?.linkedItemId,
      image_url: object?.image_url,
      image_prompt: object?.image_prompt
    };

    // Add linked module data if internal module is selected
    if (linkedModuleType === 'internal' && selectedModule) {
      // If module supports detail pages and an item ID is selected/entered
      if (moduleSupportsDetailPages(selectedModule)) {
        const itemId = selectedItemId || manualItemId.trim();
        if (itemId) {
          objectData.linkedModulePath = selectedModule;
          objectData.linkedItemId = itemId;
        }
      } else {
        // Module doesn't support detail pages, just link to module path
        objectData.linkedModulePath = selectedModule;
      }
    }

    onSave(objectData);
  };

  return (
    <div className="memory-palace-form-overlay">
      <div className="memory-palace-form">
        <div className="memory-palace-form-header">
          <h2>{object ? 'Edit Object' : 'Create New Object'}</h2>
          <button className="memory-palace-form-close" onClick={onCancel}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="memory-palace-form-content">
          <div className="memory-palace-form-field">
            <label htmlFor="object-type">Type *</label>
            <select
              id="object-type"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                if (e.target.value !== 'other') {
                  setCustomType('');
                }
              }}
              required
              className="memory-palace-form-select"
            >
              {PREDEFINED_OBJECT_TYPES.map((objType) => (
                <option key={objType} value={objType}>
                  {objType.charAt(0).toUpperCase() + objType.slice(1)}
                </option>
              ))}
            </select>
            {type === 'other' && (
              <input
                type="text"
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
                placeholder="Enter custom type"
                className="memory-palace-form-input"
                style={{ marginTop: '0.5rem' }}
                required
              />
            )}
          </div>

          <div className="memory-palace-form-field">
            <label htmlFor="object-name">Name</label>
            <input
              id="object-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Optional object name"
              className="memory-palace-form-input"
            />
          </div>

          <div className="memory-palace-form-field">
            <label htmlFor="object-description">Description</label>
            <textarea
              id="object-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={3}
              className="memory-palace-form-textarea"
            />
          </div>

          <div className="memory-palace-form-field">
            <label htmlFor="object-width">Width (px)</label>
            <input
              id="object-width"
              type="number"
              value={width}
              onChange={(e) => {
                const value = e.target.value;
                if (error) setError(null);
                if (value === '') {
                  setWidth(0);
                } else {
                  const numValue = parseInt(value, 10);
                  if (!isNaN(numValue)) {
                    setWidth(numValue);
                  }
                }
              }}
              min="20"
              max="500"
              className="memory-palace-form-input"
            />
          </div>

          <div className="memory-palace-form-field">
            <label htmlFor="object-height">Height (px)</label>
            <input
              id="object-height"
              type="number"
              value={height}
              onChange={(e) => {
                const value = e.target.value;
                if (error) setError(null);
                if (value === '') {
                  setHeight(0);
                } else {
                  const numValue = parseInt(value, 10);
                  if (!isNaN(numValue)) {
                    setHeight(numValue);
                  }
                }
              }}
              min="20"
              max="500"
              className="memory-palace-form-input"
            />
          </div>

          {object?.image_url && (
            <div className="memory-palace-form-field">
              <label>Object Image</label>
              <div className="memory-palace-form-image-preview">
                <img 
                  src={object.image_url} 
                  alt={object.image_prompt || 'Object image'} 
                  className="memory-palace-form-image-preview-img"
                />
                <button
                  type="button"
                  onClick={() => {
                    const updatedObject = { ...object };
                    delete updatedObject.image_url;
                    delete updatedObject.image_prompt;
                    onSave(updatedObject);
                  }}
                  className="memory-palace-form-image-delete-btn"
                >
                  Delete Image
                </button>
              </div>
            </div>
          )}

          <div className="memory-palace-form-field">
            <label>Link to Module</label>
            <div className="memory-palace-form-radio-group">
              <label className="memory-palace-form-radio-label">
                <input
                  type="radio"
                  name="linkedModuleType"
                  value="none"
                  checked={linkedModuleType === 'none'}
                  onChange={(e) => {
                    setLinkedModuleType('none');
                    setSelectedModule('');
                    setSelectedItemId('');
                    setManualItemId('');
                    setItemSearchTerm('');
                    setItemSearchResults([]);
                  }}
                  className="memory-palace-form-radio"
                />
                <span>None</span>
              </label>
              <label className="memory-palace-form-radio-label">
                <input
                  type="radio"
                  name="linkedModuleType"
                  value="internal"
                  checked={linkedModuleType === 'internal'}
                  onChange={(e) => {
                    setLinkedModuleType('internal');
                  }}
                  className="memory-palace-form-radio"
                />
                <span>Link to Module</span>
              </label>
            </div>
            
            {linkedModuleType === 'internal' && (
              <div style={{ marginTop: '0.5rem' }}>
                <select
                  value={selectedModule}
                  onChange={(e) => {
                    setSelectedModule(e.target.value);
                    setSelectedItemId('');
                    setManualItemId('');
                    setItemSearchTerm('');
                    setItemSearchResults([]);
                  }}
                  className="memory-palace-form-select"
                >
                  <option value="">Select a module...</option>
                  {modules.map((module) => (
                    <option key={module.id} value={module.path}>
                      {module.label}
                    </option>
                  ))}
                </select>
                
                {showItemSearch && selectedModule && (
                  <div style={{ marginTop: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#333333' }}>
                      Search and select item (or enter ID manually):
                    </label>
                    <input
                      type="text"
                      value={itemSearchTerm}
                      onChange={(e) => setItemSearchTerm(e.target.value)}
                      placeholder={`Search ${selectedModule === '/people' ? 'people' : selectedModule === '/notes' ? 'notes' : selectedModule === '/knowledge' ? 'knowledge categories' : selectedModule === '/whiteboard' ? 'whiteboards' : selectedModule === '/books' ? 'books' : 'novels'}...`}
                      className="memory-palace-form-input"
                      style={{ marginBottom: '0.5rem' }}
                    />
                    
                    {searchingItems && (
                      <div style={{ fontSize: '0.75rem', color: '#666666', marginBottom: '0.5rem' }}>Searching...</div>
                    )}
                    
                    {!searchingItems && itemSearchResults.length > 0 && (
                      <select
                        value={selectedItemId}
                        onChange={(e) => {
                          setSelectedItemId(e.target.value);
                          setManualItemId('');
                        }}
                        className="memory-palace-form-select"
                        style={{ marginBottom: '0.5rem' }}
                      >
                        <option value="">Select an item...</option>
                        {itemSearchResults.map((item) => {
                          const idField = getModuleIdField(selectedModule);
                          const itemId = idField ? (item as any)[idField] : '';
                          let displayName = '';
                          if (selectedModule === '/people') {
                            displayName = (item as Person).name;
                          } else if (selectedModule === '/notes') {
                            displayName = (item as Note).title;
                          } else if (selectedModule === '/knowledge') {
                            displayName = (item as KnowledgeCategory).category_name;
                          } else if (selectedModule === '/whiteboard') {
                            displayName = (item as Whiteboard).name;
                          } else if (selectedModule === '/books') {
                            displayName = (item as Book).title;
                          } else {
                            displayName = (item as Novel).title;
                          }
                          return (
                            <option key={itemId} value={String(itemId)}>
                              {displayName}
                            </option>
                          );
                        })}
                      </select>
                    )}
                    
                    <div style={{ fontSize: '0.75rem', color: '#666666', marginBottom: '0.5rem' }}>
                      Or enter {selectedModule === '/people' ? 'person' : selectedModule === '/notes' ? 'note' : selectedModule === '/knowledge' ? 'category' : selectedModule === '/whiteboard' ? 'whiteboard' : selectedModule === '/books' ? 'book' : 'novel'} ID manually:
                    </div>
                    <input
                      type="text"
                      value={manualItemId}
                      onChange={(e) => {
                        setManualItemId(e.target.value);
                        setSelectedItemId('');
                      }}
                      placeholder={`Enter ${selectedModule === '/people' ? 'person' : selectedModule === '/notes' ? 'note' : selectedModule === '/knowledge' ? 'category' : selectedModule === '/whiteboard' ? 'whiteboard' : selectedModule === '/books' ? 'book' : 'novel'} ID`}
                      className="memory-palace-form-input"
                    />
                    <div className="memory-palace-form-hint">
                      Leave empty to link to the module list page
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="memory-palace-form-error">{error}</div>
          )}

          <div className="memory-palace-form-actions">
            <button type="button" onClick={onCancel} className="memory-palace-form-btn cancel">
              Cancel
            </button>
            <button type="submit" className="memory-palace-form-btn save">
              {object ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ObjectForm;

